// ─────────────────────────────────────────────────────────────
//  siteConfig — the handful of real-world endpoints the site
//  points at. Edit these in ONE place instead of hunting through
//  components. Empty string ('') means "not configured yet" — the
//  UI degrades to a graceful "coming soon" state, never a broken link.
// ─────────────────────────────────────────────────────────────

// ── config from env ──────────────────────────────────────────
// Personal identifiers (emails, phone numbers, booking link) come from env
// vars — a gitignored `.env` locally and the host's env vars (Amplify) in prod —
// so NOTHING personal is committed to source. See `.env.example` for the keys.
const csv = (v?: string): string[] => (v || '').split(',').map(s => s.trim()).filter(Boolean);

// ── contact ──────────────────────────────────────────────────
// Berti's real inbox — where lesson inquiries should land. From env.
export const CONTACT_EMAIL = process.env.REACT_APP_CONTACT_EMAIL || '';

// Plain mailto for the "or email directly" fallback link (only rendered when set).
export const CONTACT_MAILTO = CONTACT_EMAIL ? `mailto:${CONTACT_EMAIL}` : '';

// ── editable content backend (Phase 2) ───────────────────────
// Base URL of the content/auth API (API Gateway). While empty, the site
// runs off code defaults + a browser-local override store (edits persist
// only in that browser) — no backend required. Set this to the deployed
// API base to switch to the shared DynamoDB-backed store with history.
export const CONTENT_API_BASE = 'https://f3iazfozaf.execute-api.us-east-2.amazonaws.com';

export const CONTENT_BACKEND_READY = Boolean(CONTENT_API_BASE);

// ── contact form delivery (SES via our API) ──────────────────
// The contact form POSTs JSON to the site API's /contact route, which sends the
// inquiry to Berti (+ debug cc) via SES — the message is delivered server-side;
// the visitor's mail client is never opened. Mirrors the Undercurrent site's
// contact flow. If the API base is empty, the form gracefully falls back to
// CONTACT_MAILTO so it's never a dead end.
export const CONTACT_FORM_ENDPOINT = CONTENT_API_BASE ? `${CONTENT_API_BASE}/contact` : '';
export const CONTACT_FORM_READY = Boolean(CONTACT_FORM_ENDPOINT);

// ── scheduling (Google Calendar via the API's /booking route) ─
// The Schedule page POSTs a lesson request here; the Lambda creates the event on
// Berti's Google Calendar (once its GCAL_SA_KEY + GCAL_CALENDAR_ID env are set).
// While the backend is unconfigured it returns 503 and the page shows an
// email-us fallback, so this is safe to ship before the calendar is wired.
export const BOOKING_ENDPOINT = CONTENT_API_BASE ? `${CONTENT_API_BASE}/booking` : '';

// ── scheduling (Calendly embed) ──────────────────────────────
// Berti's Calendly booking link (e.g. https://calendly.com/berti/cello-lesson).
// From env. While empty, the Schedule page shows an email-us fallback.
export const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || '';

// Who may enter edit mode. Sourced from env so no personal emails/numbers are
// committed. In LOCAL/preview mode this gates the sign-in UI; in remote mode the
// backend (Lambda EDIT_EMAILS env) is the real enforcer. Phone matching is
// format-flexible (see auth normalization), so store any reasonable form.
export const EDIT_ALLOWLIST = {
  emails: csv(process.env.REACT_APP_EDITOR_EMAILS),
  signalNumbers: csv(process.env.REACT_APP_EDITOR_SIGNAL),
};

// Signal sign-in is toggled off for now — email code (with emoji match) is the
// only method. Flip to true (and wire the Lambda /auth/signal routes) to bring
// it back.
export const SIGNAL_ENABLED = false;

// Everything the editor writes lives here in local mode.
export const CONTENT_LOCAL_KEY = 'evenstar-content-overrides';
export const SESSION_LOCAL_KEY = 'evenstar-session';

// ── payments ─────────────────────────────────────────────────
// Lesson payment endpoints. Scaffolding only — fill in when the
// accounts exist. Each is optional; the /pay page shows a method
// only once its link/address is set.
// All values come from env (REACT_APP_PAY_*) so no personal handles are
// committed. Each is optional; the /pay page shows a method only once its
// value is set. Venmo + Zelle are the zero-fee / low-fee, no-code options
// most local families already use; Stripe is the card + recurring path.
const venmoRaw = (process.env.REACT_APP_PAY_VENMO || '').trim();
// Accept either a bare handle ("Bertis-Music", with or without @) or a full URL.
const venmoHandle = venmoRaw.replace(/^@/, '').replace(/^https?:\/\/(account\.)?venmo\.com\/(u\/)?/i, '');

export const PAYMENTS = {
  // Venmo — most-used, low fee. Set REACT_APP_PAY_VENMO to the business
  // profile handle. Renders a "Pay with Venmo" deep link + the @handle.
  venmoHandle,
  venmoLink: venmoHandle ? `https://venmo.com/u/${venmoHandle}` : '',

  // Zelle — zero fees, bank-to-bank. No web link exists; set
  // REACT_APP_PAY_ZELLE to the enrolled email or phone shown to payers.
  zelleContact: (process.env.REACT_APP_PAY_ZELLE || '').trim(),

  // Stripe — card + automated recurring tuition. Paste a no-code Payment
  // Link (Stripe dashboard → Payment Links → New) into REACT_APP_PAY_STRIPE.
  stripePaymentLink: (process.env.REACT_APP_PAY_STRIPE || '').trim(),

  // PayPal — a PayPal.Me link (guest card payments, no account needed).
  paypalMeLink: (process.env.REACT_APP_PAY_PAYPAL || '').trim(),

  // Bitcoin — FOR FUN. A plain on-chain receive address; /pay shows it copyable.
  bitcoinAddress: (process.env.REACT_APP_PAY_BTC || '').trim(),
};

// True when at least one online method is live.
export const HAS_ONLINE_PAYMENTS =
  Boolean(PAYMENTS.venmoLink) ||
  Boolean(PAYMENTS.zelleContact) ||
  Boolean(PAYMENTS.stripePaymentLink) ||
  Boolean(PAYMENTS.paypalMeLink) ||
  Boolean(PAYMENTS.bitcoinAddress);
